import React, { useState } from "react";
import { useFetch } from "@/hooks/useApi";
import api, { formatApiErrorDetail, downloadFile } from "@/lib/api";
import { PageHeader, Loader, StatusPill, Plate, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { num, ukDate, todayISO } from "@/lib/format";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function DriverStops() {
  const { data: me } = useFetch("/drivers/me");
  const { data: stops, loading, refetch } = useFetch("/stops");
  const [form, setForm] = useState({ date: todayISO(), submitted_stops: "", van_reg: "", notes: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/stops", { ...form, van_reg: form.van_reg || me?.assigned_van_reg, submitted_stops: parseInt(form.submitted_stops, 10) });
      toast.success("Stops submitted for approval");
      setForm({ date: todayISO(), submitted_stops: "", van_reg: "", notes: "" });
      refetch();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (loading) return <Loader />;

  const downloadPayslip = async () => {
    try { await downloadFile("/payslips/me", "my_weekly_payslip.pdf"); }
    catch { toast.error("Could not generate payslip"); }
  };

  return (
    <div className="fp-fade-in max-w-2xl mx-auto">
      <PageHeader title="My Stops" subtitle="Submit your daily deliveries for admin approval" testId="driver-stops-page">
        <Button variant="outline" onClick={downloadPayslip} data-testid="download-payslip-button">
          <Icons.FileDown className="h-4 w-4 mr-1.5" /> Weekly Payslip
        </Button>
      </PageHeader>

      <Card className="p-6 mb-6">
        <form onSubmit={submit} className="space-y-4" data-testid="submit-stops-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={set("date")} className="mt-1.5" data-testid="stop-date-input" /></div>
            <div><Label>Number of Stops</Label><Input type="number" value={form.submitted_stops} onChange={set("submitted_stops")} required className="mt-1.5 font-mono text-lg" data-testid="stop-count-input" /></div>
            <div>
              <Label>Van</Label>
              <Input value={form.van_reg || me?.assigned_van_reg || ""} onChange={set("van_reg")} className="mt-1.5 font-mono" placeholder={me?.assigned_van_reg} />
            </div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} className="mt-1.5" placeholder="Anything the admin should know?" /></div>
          <Button type="submit" className="w-full h-12" data-testid="submit-stops-button"><Icons.Send className="h-4 w-4 mr-2" /> Submit Stops</Button>
        </form>
      </Card>

      <h3 className="font-display font-semibold mb-3">Recent Submissions</h3>
      <div className="space-y-3">
        {(stops || []).length === 0 && <EmptyState icon="MapPin" title="No submissions yet" />}
        {(stops || []).map((s) => (
          <Card key={s.id} className="p-4 flex items-center justify-between" data-testid={`my-stop-${s.id}`}>
            <div>
              <p className="font-mono text-sm text-muted-foreground">{ukDate(s.date)}</p>
              <p className="mt-1">
                <span className="font-mono font-bold text-lg">{num(s.submitted_stops)}</span>
                <span className="text-muted-foreground text-sm"> submitted</span>
                {s.approved_stops != null && <span className="text-emerald-400"> → {num(s.approved_stops)} approved</span>}
              </p>
            </div>
            <StatusPill status={s.status} />
          </Card>
        ))}
      </div>
    </div>
  );
}
